'use client'

import { XIcon } from 'lucide-react'
import { memo, useCallback, useState, useEffect } from 'react'
import type { FC } from 'react'

import { BaseSelectProps, ChevronIcon, MSItemComponent } from './SelectContent'
import { Badge } from '../badge/Badge'
import { Button } from '../button/Button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandList } from '../command/Command'
import { cn } from '../lib/cn'
import { MarqueeText } from '../marquee-text'
import { Popover, PopoverContent, PopoverTrigger } from '../popover/Popover'

interface MultiSelectProps extends BaseSelectProps {
  value: string[]
  onValueChange: (value: string[]) => void
  triggerClassName?: string
}

const EmptyFirstOptions: React.FC = () => null

export const MultiSelect: FC<MultiSelectProps> = memo(
  ({
    value,
    onValueChange,
    options,
    placeholder = 'All',
    disabled = false,
    modal = false,
    CustomFirstOptions = EmptyFirstOptions,
    triggerClassName,
  }) => {
    const [open, setOpen] = useState(false)
    const [isSelectedAll, setIsSelectedAll] = useState(false)

    const toggleValue = useCallback(
      (val: string) => {
        const isValAlreadySelected = value.includes(val)
        const newVal = isValAlreadySelected ? value.filter((v) => v !== val) : [...value, val]
        onValueChange(newVal)
      },
      [value, onValueChange],
    )

    const toggleSelectedAll = useCallback(
      (checked?: boolean) => {
        const shouldSelect = typeof checked === 'boolean' ? checked : !isSelectedAll
        setIsSelectedAll(shouldSelect)

        const allValues = shouldSelect ? options.map((option) => option.value || '-') : []
        onValueChange(allValues)
      },
      [options, onValueChange, isSelectedAll],
    )

    useEffect(() => {
      const allSelected = options.length > 0 && value.length === options.length

      if (allSelected !== isSelectedAll) {
        setIsSelectedAll(allSelected)
      }
    }, [value, options, isSelectedAll])

    const onDelete = useCallback(
      (val: string) => (event: React.MouseEvent<SVGSVGElement>) => {
        event.stopPropagation()
        toggleValue(val)
      },
      [toggleValue],
    )

    const renderTrigger = useCallback(
      () =>
        value.length > 0 ? (
          <div className='flex justify-between items-center w-full'>
            <div className='relative flex flex-wrap items-center gap-1 overflow-hidden'>
              {value.slice(0, 3).map((value) => {
                const option = options.find((o) => o.value === value) || {
                  label: value,
                  value,
                }
                const IconComponent = option?.icon

                return (
                  <Badge
                    className='bg-muted text-foreground rounded-md max-w-inherit'
                    wrapperClassName='max-w-full'
                    style={{ maxWidth: 'stretch' }}
                    variant='info'
                    key={value}
                  >
                    {IconComponent && <IconComponent className='h-4 w-4 mr-2' />}
                    <MarqueeText>{option?.label}</MarqueeText>
                    <XIcon
                      className='ml-1 max-h-3 max-w-3 cursor-pointer text-muted-foreground hover:text-foreground'
                      onClick={onDelete(value)}
                    />
                  </Badge>
                )
              })}
              {value.length > 3 && (
                <Badge className={cn('bg-transparent text-foreground border-foreground/1 hover:bg-transparent')}>
                  {`+ ${value.length - 3} more`}
                </Badge>
              )}
            </div>
            <div className='flex items-center justify-between'>
              <ChevronIcon open={open} />
            </div>
          </div>
        ) : (
          <div className='flex items-center justify-between w-full mx-auto'>
            <span className='text-sm text-muted-foreground font-normal'>{placeholder}</span>
            <ChevronIcon open={open} />
          </div>
        ),
      [onDelete, open, options, placeholder, value],
    )

    return (
      <Popover open={open} onOpenChange={setOpen} modal={modal}>
        <PopoverTrigger asChild>
          <Button
            disabled={disabled}
            variant='outline'
            className={cn(
              'flex min-h-[38px] h-auto px-3 py-1.5 rounded-lg border-input items-center justify-between bg-card hover:bg-inherit [&_svg]:pointer-events-auto',
              triggerClassName,
            )}
          >
            {renderTrigger()}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0 w-(--radix-popover-trigger-width)' align='start'>
          <Command>
            <CommandInput
              placeholder='Search...'
              wrapperClasses='border-b-input'
              isSelectedAll={isSelectedAll}
              onIsSelectedAll={toggleSelectedAll}
            />
            <CommandList
              className={cn(
                modal && 'max-h-[30vh] overflow-y-auto multiselect-scrollbar overscroll-behavior-y-contain',
              )}
            >
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CustomFirstOptions options={options} value={value} toggleValue={toggleValue} onlyOneSelect={false} />

                {options.map((option) => (
                  <MSItemComponent
                    value={value}
                    onlyOneSelect={false}
                    toggleValue={toggleValue}
                    key={option.value}
                    option={option}
                  />
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

MultiSelect.displayName = 'MultiSelect'
